import { get } from 'lodash';

/**
 * Aggregates assignment stats (totalHours, totalUnitCount, unitAmountGoal)
 * from multiple assignmentIds.
 *
 * For unit completion, calculates the average completion percentage across
 * assignments that have a valid unitAmountGoal.
 *
 * Supports both the new `assignmentIds` array and the legacy `assignmentId` field
 * for backward compatibility.
 *
 * @param {Object} task - The timeline task object
 * @param {Object} statsMap - The assignment stats map keyed by assignmentId
 * @returns {{ totalHours: number, totalUnitCount: number, unitAmountGoal: number, unitCompletionPercentage: number | null }}
 */
export const aggregateAssignmentStats = (task, statsMap) => {
  const assignmentIds = task.assignmentIds || (task.assignmentId ? [task.assignmentId] : []);

  let totalHours = 0;
  let totalUnitCount = 0;
  let unitAmountGoal = 0;
  let completionSum = 0;
  let completionCount = 0;

  assignmentIds.forEach((assignmentId) => {
    const assignmentData = get(statsMap, assignmentId);
    if (!assignmentData) return;

    totalHours += assignmentData.totalHours || 0;

    const assignmentUnitCount = assignmentData.totalUnitCount || 0;
    const assignmentUnitGoal = assignmentData.unitAmountGoal || 0;

    totalUnitCount += assignmentUnitCount;
    unitAmountGoal += assignmentUnitGoal;

    if (assignmentUnitGoal > 0) {
      const percentage = Math.min((assignmentUnitCount / assignmentUnitGoal) * 100, 100);
      completionSum += percentage;
      completionCount += 1;
    }
  });

  const unitCompletionPercentage = completionCount > 0
    ? completionSum / completionCount
    : null;

  return {
    totalHours,
    totalUnitCount,
    unitAmountGoal,
    unitCompletionPercentage,
  };
};
